import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import { logger } from "@/lib/logger";

interface GoogleDriveStatus {
  primary: {
    connected: boolean;
    email?: string;
    hasDriveAccess: boolean;
    sharedDrivesCount?: number;
    sharedFileCount?: number;
    error?: string;
    needsReauth?: boolean;
    driveScopeGranted?: boolean;
    scopes?: any;
  };
  secondary: {
    connected: boolean;
    email?: string;
    hasDriveAccess: boolean;
    sharedDrivesCount?: number;
    sharedFileCount?: number;
    error?: string;
    needsReauth?: boolean;
    driveScopeGranted?: boolean;
    scopes?: any;
  };
}

interface UseGoogleDriveStatusParams {
  enabled?: boolean;
}

/**
 * Hook to fetch Google Drive status for both primary and secondary accounts
 * Uses the same patterns as other API hooks in the codebase
 */
export const useGoogleDriveStatus = (params: UseGoogleDriveStatusParams = {}) => {
  const { session } = useUniversalAuth();
  const { enabled = true } = params;

  const notConnectedStatus: GoogleDriveStatus = {
    primary: { connected: false, hasDriveAccess: false },
    secondary: { connected: false, hasDriveAccess: false },
  };

  return useQuery({
    queryKey: ["google-drive-status"],
    queryFn: async () => {
      const token = session?.access_token;
      if (!token) {
        throw new Error("No authentication token");
      }

      const driveStatusResponse = await fetch("/api/unifiedtime/getDrive/status", {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!driveStatusResponse.ok) {
        // Expected when Drive is not connected, not authorized, or bad request (e.g. not configured)
        const status = driveStatusResponse.status;
        if (status === 400 || status === 401 || status === 403 || status === 404 || status === 422) {
          if (process.env.NODE_ENV === "development") {
            logger.info("Google Drive status: not connected or not authorized", {
              status,
              statusText: driveStatusResponse.statusText,
            });
          }
          return notConnectedStatus;
        }
        // Unexpected server/network error - log and throw
        logger.error("Google Drive status: request failed", {
          status,
          statusText: driveStatusResponse.statusText,
        });
        throw new Error("Failed to get Drive status");
      }

      const driveStatus = await driveStatusResponse.json();

      // Convert the API response to our hook's format
      const status: GoogleDriveStatus = {
        primary: {
          connected: driveStatus.primary.connected,
          email: driveStatus.primary.email,
          hasDriveAccess: driveStatus.primary.hasDriveAccess,
          sharedDrivesCount: driveStatus.primary.driveCount || 0,
          sharedFileCount: driveStatus.primary.sharedFileCount || 0,
          driveScopeGranted: driveStatus.primary.status !== "needs_reauth",
          needsReauth: driveStatus.primary.status === "needs_reauth",
          error: driveStatus.primary.error,
          scopes: driveStatus.primary.scopes,
        },
        secondary: {
          connected: driveStatus.secondary.connected,
          email: driveStatus.secondary.email,
          hasDriveAccess: driveStatus.secondary.hasDriveAccess,
          sharedDrivesCount: driveStatus.secondary.driveCount || 0,
          sharedFileCount: driveStatus.secondary.sharedFileCount || 0,
          driveScopeGranted: driveStatus.secondary.status !== "needs_reauth",
          needsReauth: driveStatus.secondary.status === "needs_reauth",
          error: driveStatus.secondary.error,
          scopes: driveStatus.secondary.scopes,
        },
      };

      return status;
    },
    enabled: enabled && !!session?.access_token,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Reduce retry attempts to 1
    refetchOnMount: false, // Don't refetch on mount to avoid excessive calls
    refetchOnWindowFocus: false, // Don't refetch on window focus to avoid excessive calls
  });
};
